import React, { Suspense, useEffect, useMemo } from "react";
import { Dashboard } from "../layout";
import TopNavShell from "../layout/TopNav/TopNavShell";
import TopMenu from "../layout/TopNav/TopMenu";
import { Header, NavMenu } from "../components/body";
import { getDefaultRoute } from "../components/body/menuConfig";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { logo } from "../assets";
import { useAppContext } from "context";
import { useDocumentTitle } from "hooks";
import Loading from "../layout/Loading";
import NotFoundPage from "./NotFoundPage";
import NoAccessPage from "./NoAccessPage";
import FyAlert from "./Accounts/FiscalYear/FyAlert";
import BizContext from "./BizContext/BizContext";
import {
  homeRoutes,
  salesRoutes,
  catalogRoutes,
  inventoryRoutes,
  customerRoutes,
  accountsRoutes,
  reportRoutes,
  setupRoutes,
  crmRoutes,
  ecommerceRoutes,
} from "./routes";

const PageWrapper = ({ component: Component, title, props }) => {
  useDocumentTitle(title);
  return <Component {...(props || {})} />;
};

const renderRoutes = (routeConfigs) =>
  routeConfigs
    .filter((r) => !("enabled" in r) || r.enabled)
    .map((r) => (
      <Route
        key={r.path}
        path={r.path}
        element={<PageWrapper component={r.element} title={r.title} props={r.props} />}
      />
    ));

const SiteRoute = () => {
  const navigate = useNavigate();
  const { authUser, access, settings, selCompany } = useAppContext();
  const isReady = selCompany && Object.keys(access).length > 0;
  const isModern = settings?.uiTemplate === "modern";
  const Shell = isModern ? TopNavShell : Dashboard;
  const menu = isModern ? <TopMenu /> : <NavMenu />;

  // The user's first accessible page (per their permissions/menu). Drives the
  // landing redirect so a user without dashboard access never lands on a route
  // they can't reach. Null = no accessible module → show the No-Access page.
  const defaultRoute = useMemo(
    () => (isReady ? getDefaultRoute(access, settings) : null),
    [isReady, access, settings]
  );

  useEffect(() => {
    // Move off the auth screen after login; the "/" route resolves the landing.
    if (authUser && window.location.pathname === "/auth") navigate("/", { replace: true });
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser]);

  return (
    <Shell menu={menu} header={<Header />} logo={logo}>
      {/* ------- Fiscal year alert -------  */}
      <FyAlert />

      {/* -------- Business Context --------  */}
      <BizContext />

      <Suspense fallback={<Loading />}>
        <Routes>
          <Route
            path="/"
            element={
              !isReady ? (
                <Loading />
              ) : (
                <Navigate to={defaultRoute ?? "/no_access"} replace />
              )
            }
          />

          <Route path="/no_access" element={<NoAccessPage />} />

          {renderRoutes(homeRoutes(access, settings))}
          {renderRoutes(salesRoutes(access, settings))}
          {renderRoutes(catalogRoutes(access, settings))}
          {renderRoutes(inventoryRoutes(access, settings))}
          {renderRoutes(customerRoutes(access, settings))}
          {renderRoutes(accountsRoutes(access, settings))}
          {renderRoutes(reportRoutes(access, settings))}
          {renderRoutes(setupRoutes(access, settings))}
          {renderRoutes(crmRoutes(access, settings))}
          {renderRoutes(ecommerceRoutes(access, settings))}

          <Route path="*" element={isReady ? <NotFoundPage /> : <Loading />} />
        </Routes>
      </Suspense>
    </Shell>
  );
};

export default SiteRoute;
